import {
  Briefcase,
  Building2,
  Car,
  ChevronDown,
  ChevronUp,
  FileText,
  HelpCircle,
  Home,
  Info,
  Package,
  Phone,
  Shield,
  Store,
  Truck,
  Users,
  Zap,
} from "lucide-react";
import { useState } from "react";
import { HiArrowNarrowRight } from "react-icons/hi"; // Add this import at the top
import { useNavigate } from "react-router-dom";

interface MegamenuProps {
  onClose: () => void;
  open: boolean;
}

const sections = [
  {
    id: "drive",
    isPromotion: true,
    items: [
      { icon: <Home size={18} />, label: "Home" },
      { icon: <Briefcase size={18} />, label: "Services" },
    ],
    promotionImage:
      "https://plus.unsplash.com/premium_photo-1786941170814-59b7d27d539f?q=80&w=723&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    promotionText: "",
    title: "",
  },
  {
    id: "scooters",
    isPromotion: true,
    items: [
      { icon: <Zap size={18} />, label: "Updates" },
      { icon: <Phone size={18} />, label: "Reach Out" },
    ],
    promotionImage:
      "https://images.unsplash.com/photo-1786800054088-f017f09fc650?q=80&w=870&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D?w=400&h=250&fit=crop&crop=center",
    promotionText: "",
    title: "",
  },
];

const opportunities = [
  {
    icon: <Briefcase size={32} />,
    label: "Become Part of us",
    subtitle: "Work with us in dynamic environment",
  },
  {
    icon: <Info size={32} />,
    label: "Know more about us",
    subtitle: "Learn about our mission, vision, and values",
  },
];

export default function MegaMenu({ onClose, open }: MegamenuProps) {
  const [openSection, setOpenSection] = useState<null | string>(null);
  const navigate = useNavigate();

  const handleLinkClick = (label: string) => {
    onClose();

    switch (label) {
      case "Become Part of us":
        navigate("/pages/about-us/careers");
        break;
      case "Home":
        navigate("/");
        break;
      case "Know more about us":
        navigate("/pages/about-us/who-we-are");
        break;
      case "Reach Out":
        navigate("/contact-us");
        break;
      case "Services":
        navigate("/pages/about-us/for-communities");
        break;
      case "Updates":
        navigate("/pages/about-us/updates");
        break;
      default:
        // Optional fallback
        console.warn("No route defined for:", label);
    }
  };

  const toggleSection = (section: string) => {
    setOpenSection((prev) => (prev === section ? null : section));
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-base-100 bg-opacity-95 backdrop-blur-sm">
      {/* Close button (fixed at the top) */}
      <div className="sticky top-0 z-10 flex justify-end bg-base-100 bg-opacity-95 p-6">
        <button
          aria-label="Close menu"
          className="btn btn-circle btn-ghost text-2xl hover:bg-error hover:text-error-content"
          onClick={onClose}
        >
          ×
        </button>
      </div>

      {/* Scrollable content */}
      <div className="w-full flex-1 overflow-y-auto px-4 pb-8">
        {/* Desktop */}
        <div className="hidden h-full grid-cols-3 gap-8 md:grid">
          <div className="col-span-2 grid grid-cols-2 gap-8">
            {sections.map((section) => (
              <div
                className={`h-fit rounded-xl p-6 ${
                  section.isPromotion
                    ? "border border-primary/20 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent"
                    : "bg-base-200"
                }`}
                key={section.id}
              >
                {section.isPromotion && section.promotionImage ? (
                  <div className="mb-4">
                    <div className="relative overflow-hidden rounded-lg">
                      <img
                        alt={section.title}
                        className="h-32 w-full object-cover transition-transform hover:scale-105"
                        src={section.promotionImage}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                      <div className="absolute bottom-2 left-2 right-2">
                        <p className="text-sm font-medium text-white">
                          {section.promotionText}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div
                  className={`mb-3 flex items-center gap-2 text-lg font-bold ${
                    section.isPromotion ? "text-primary" : ""
                  }`}
                >
                  {section.items[0]?.icon}
                  {section.title}
                </div>

                {/* Desktop nav links (inside <ul>) */}
                <ul className="space-y-1">
                  {section.items.map((item) => (
                    <li
                      className="flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-base-300/50"
                      key={item.label}
                    >
                      <span className="text-primary">{item.icon}</span>
                      <a
                        className="link-hover link flex-1 text-sm font-medium"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleLinkClick(item.label);
                        }}
                      >
                        {item.label}
                      </a>
                      <HiArrowNarrowRight className="animate-bounce-x ml-1 text-primary" />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="flex h-fit flex-col gap-4 rounded-xl bg-base-200 p-6">
            <div className="grid gap-4">
              {opportunities.map((item) => (
                <div
                  className="flex cursor-pointer flex-col items-center rounded-lg border border-transparent bg-base-100 p-4 shadow transition-all hover:scale-105 hover:border-primary/20 hover:shadow-lg"
                  key={item.label}
                  onClick={() => handleLinkClick(item.label)}
                >
                  <div className="mb-3 text-primary">{item.icon}</div>
                  <div className="text-center font-semibold">{item.label}</div>
                  <div className="mt-1 text-center text-xs text-base-content/70">
                    {item.subtitle}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile */}
        <div className="md:hidden">
          {sections.map((section) => (
            <div
              className={`mb-4 rounded-lg p-4 ${
                section.isPromotion
                  ? "border border-primary/20 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent"
                  : "border-b border-base-content/20 pb-2"
              }`}
              key={section.id}
            >
              {section.isPromotion && section.promotionImage ? (
                <div className="mb-4">
                  <div className="relative overflow-hidden rounded-lg">
                    <img
                      alt={section.title}
                      className="h-24 w-full object-cover"
                      src={section.promotionImage}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    <div className="absolute bottom-1 left-2 right-2">
                      <p className="text-xs font-medium text-white">
                        {section.promotionText}
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}

              <button
                className={`flex w-full items-center justify-between py-2 text-left text-lg font-bold ${
                  section.isPromotion ? "text-primary" : ""
                }`}
                onClick={() => toggleSection(section.id)}
              >
                <span className="flex items-center gap-2">
                  {section.items[0]?.icon}
                  {section.title}
                </span>
                {openSection === section.id ? (
                  <ChevronUp size={18} />
                ) : (
                  <ChevronDown size={18} />
                )}
              </button>

              {openSection === section.id && (
                <ul className="mt-2 space-y-1 pl-4 text-base-content/80">
                  {section.items.map((item) => (
                    <li
                      className="flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-base-300/50"
                      key={item.label}
                    >
                      <span className="text-primary">{item.icon}</span>
                      <a
                        className="link-hover link flex-1 text-sm"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleLinkClick(item.label);
                        }}
                      >
                        {item.label}
                      </a>
                      <HiArrowNarrowRight className="animate-bounce-x ml-1 text-primary" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          <div className="mt-6 rounded-lg bg-base-200 p-4">
            <div className="mb-4 text-lg font-bold text-primary">
              Opportunities
            </div>
            <div className="grid gap-4">
              {opportunities.map((item) => (
                <div
                  className="flex cursor-pointer flex-col items-center rounded-lg border border-transparent bg-base-100 p-4 shadow transition-all hover:border-primary/20 hover:shadow-lg"
                  key={item.label}
                  onClick={() => handleLinkClick(item.label)}
                >
                  <div className="mb-2 text-primary">{item.icon}</div>
                  <div className="text-center font-semibold">{item.label}</div>
                  <div className="mt-1 text-center text-xs text-base-content/70">
                    {item.subtitle}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

